import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  DollarSign, 
  TrendingUp, 
  CheckCircle, 
  XCircle, 
  Info,
  Youtube,
  Music2,
  Instagram,
  Facebook,
  ArrowLeft,
  ExternalLink
} from 'lucide-react';

const API_URL = window.__BACKEND_URL;

/**
 * Monetization Insights Page
 * 
 * Shows:
 * - Eligibility status
 * - Platform monetization requirements (dynamic based on connected platforms)
 * - Revenue estimates (placeholder)
 */
export default function MonetizationInsights() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [platformStatus, setPlatformStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, platformRes] = await Promise.all([
        fetch(`${API_URL}/api/creator-profile`, { credentials: 'include' }),
        fetch(`${API_URL}/api/social/status`, { credentials: 'include' })
      ]);
      
      if (profileRes.ok) {
        const data = await profileRes.json();
        setEligibilityData({
          monetization_eligible: data.monetization_eligible || false,
          platforms_connected: data.platforms_connected || 0,
          total_views: data.total_views || 0,
          total_watch_hours: data.total_watch_hours || 0
        });
      }
      
      if (platformRes.ok) {
        setPlatformStatus(await platformRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl" data-testid="monetization-insights-page">
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  const isEligible = eligibilityData?.monetization_eligible;
  const connections = platformStatus?.connections || {};

  // All platform definitions with monetization requirements
  const allPlatforms = [
    {
      key: 'youtube',
      name: 'YouTube',
      icon: Youtube,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
      requirements: [
        { label: '1,000+ Subscribers', met: false },
        { label: '4,000 Watch Hours (12 months)', met: false },
        { label: 'AdSense Account', met: false }
      ]
    },
    {
      key: 'tiktok',
      name: 'TikTok',
      icon: Music2,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10',
      requirements: [
        { label: '10,000+ Followers', met: false },
        { label: '100,000 Video Views (30 days)', met: false },
        { label: '18+ Years Old', met: false }
      ]
    },
    {
      key: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      requirements: [
        { label: 'Professional Account', met: false },
        { label: 'Meet Partner Eligibility', met: false },
        { label: 'Follow Community Guidelines', met: false }
      ]
    },
    {
      key: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      requirements: [
        { label: '10,000 Page Followers', met: false },
        { label: '600,000 Total Minutes Viewed (60 days)', met: false },
        { label: '5+ Active Videos', met: false }
      ]
    }
  ];

  // Filter to show only connected platforms, or all if none connected
  const connectedPlatforms = allPlatforms.filter(p => connections[p.key]?.is_connected);
  const platformsToShow = connectedPlatforms.length > 0 ? connectedPlatforms : allPlatforms;
  const hasConnectedPlatforms = connectedPlatforms.length > 0;

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="monetization-insights-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate(-1)}
          className="text-[#8A93A6] hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
      </div>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-green-500" />
          Monetization Insights
        </h1>
        <p className="text-[#8A93A6]">Track your eligibility and revenue potential across platforms</p>
      </div>

      {/* Eligibility Status */}
      <Card className="glass mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              Eligibility Status
            </CardTitle>
            <Badge 
              variant="outline" 
              className={isEligible ? 'border-green-500/30 text-green-400' : 'border-yellow-500/30 text-yellow-400'}
            >
              {isEligible ? (
                <><CheckCircle className="h-3 w-3 mr-1" />Eligible</>
              ) : (
                <><Info className="h-3 w-3 mr-1" />Check Requirements</>
              )}
            </Badge>
          </div>
          <CardDescription>Your current monetization eligibility across connected platforms</CardDescription>
        </CardHeader>
        <CardContent>
          {isEligible ? (
            <Alert className="bg-green-500/10 border-green-500/30">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <AlertDescription className="text-green-400">
                You meet the basic eligibility requirements for monetization. Connect your platforms to start earning.
              </AlertDescription>
            </Alert>
          ) : (
            <Alert className="bg-yellow-500/10 border-yellow-500/30">
              <Info className="h-4 w-4 text-yellow-500" />
              <AlertDescription className="text-yellow-400">
                Review the platform requirements below to see what's needed for monetization eligibility.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Platform Requirements */}
      {!hasConnectedPlatforms && (
        <Alert className="bg-blue-500/10 border-blue-500/30 mb-6">
          <Info className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-400">
            No platforms connected yet. Showing all platform requirements. Connect a platform to see personalized insights.
          </AlertDescription>
        </Alert>
      )}

      <div className={`grid grid-cols-1 gap-6 mb-6 ${
        platformsToShow.length === 1 ? 'md:grid-cols-1 max-w-md' :
        platformsToShow.length === 2 ? 'md:grid-cols-2' :
        platformsToShow.length === 3 ? 'md:grid-cols-3' :
        'md:grid-cols-2 lg:grid-cols-4'
      }`}>
        {platformsToShow.map((platform) => {
          const isConnected = connections[platform.key]?.is_connected;
          return (
            <Card key={platform.key} className="glass">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className={`p-2 rounded-lg ${platform.bgColor}`}>
                    <platform.icon className={`h-5 w-5 ${platform.color}`} />
                  </div>
                  {platform.name}
                  {isConnected && (
                    <Badge className="ml-auto bg-green-500/20 text-green-400 text-xs">
                      Connected
                    </Badge>
                  )}
                </CardTitle>
                <CardDescription>Monetization requirements</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3">
                  {platform.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      {req.met ? (
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      )}
                      <span className={req.met ? 'text-white' : 'text-[#8A93A6]'}>{req.label}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Revenue Estimates (Placeholder) */}
      <Card className="glass">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-500" />
            Estimated Revenue Potential
          </CardTitle>
          <CardDescription>Based on your current metrics and industry averages</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-black/40 border border-white/10 text-center">
              <p className="text-2xl font-bold text-white">$0</p>
              <p className="text-xs text-[#8A93A6] mt-1">Monthly Estimate</p>
              <p className="text-xs text-yellow-500 mt-2">Connect platforms to calculate</p>
            </div>
            <div className="p-4 rounded-lg bg-black/40 border border-white/10 text-center">
              <p className="text-2xl font-bold text-white">$0</p>
              <p className="text-xs text-[#8A93A6] mt-1">Per 1K Views (CPM)</p>
              <p className="text-xs text-yellow-500 mt-2">Industry avg: $2-5</p>
            </div>
            <div className="p-4 rounded-lg bg-black/40 border border-white/10 text-center">
              <p className="text-2xl font-bold text-white">N/A</p>
              <p className="text-xs text-[#8A93A6] mt-1">Growth Trend</p>
              <p className="text-xs text-yellow-500 mt-2">Needs more data</p>
            </div>
          </div>
          
          <div className="mt-6 flex justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/app/account/creator-profile')}
              className="border-white/10"
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Connect More Platforms
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
